// Experience-attempt diagnostics cache (MV-1077).
//
// When a user can't enter an experience — no game machines, a GameCast/stream
// failure, or a UE crash — the app navigates them back to the event-selection
// screen. That navigation unmounts MytaverseEventProvider + the GameCast hooks,
// so the streaming correlation ids (streamGroupId, streamSessionId/ARN, region,
// session key) and the failure reason are LOST from React state. A report filed
// from the event screen would then have nothing for the backend to correlate.
//
// This module persists a small "last experience attempt" record so the Report
// It dialog (opened from the profile dropdown, outside the experience) can
// attach those ids + the failure reason. The detailed client logs are NOT
// stored here — they already stream to Sentry tagged by websocketSessionId, and
// the backend analysis pulls them by that id.
//
// Backed by sessionStorage: per-tab (no cross-tab contamination), survives the
// client-side navigation back to /events and same-tab reloads, and clears when
// the tab closes. All access is guarded so a disabled/again-private store can
// never throw into the streaming path.

import { ReportCorrelationEntities } from './reportItApi';

const STORAGE_KEY = 'mv.reportit.experienceDiagnostics.v1';
// Stale records (e.g. a yesterday's attempt) must not be misattributed to a
// fresh report. 30 minutes comfortably covers a report filed right after a
// failed entry while excluding unrelated old sessions.
const DEFAULT_TTL_MS = 30 * 60 * 1000;

export type ExperienceOutcome = 'attempting' | 'active' | 'failed';

export interface ExperienceDiagnostics {
  /** The experience (event) the user was entering, when known. */
  eventId?: string;
  /** The project (world) when known. */
  worldId?: string;
  /** Streaming correlation ids captured while the attempt was live. */
  entities: ReportCorrelationEntities;
  outcome: ExperienceOutcome;
  /** Why the attempt ended (a navigation/notification reason key or message). */
  failureReason?: string;
  /** ISO timestamp of the last update — drives the staleness check. */
  updatedAt: string;
}

function getStore(provided?: Storage): Storage | null {
  if (provided) {
    return provided;
  }

  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    // Access to storage can throw (privacy mode / sandboxed iframe).
    return null;
  }
}

function safeRead(store: Storage | null): ExperienceDiagnostics | null {
  if (!store) {
    return null;
  }

  try {
    const raw = store.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as ExperienceDiagnostics;

    if (!parsed || typeof parsed !== 'object' || typeof parsed.updatedAt !== 'string') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function safeWrite(store: Storage | null, record: ExperienceDiagnostics): void {
  if (!store) {
    return;
  }

  try {
    store.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota / disabled store — diagnostics are best-effort, never fatal.
  }
}

function isStale(record: ExperienceDiagnostics, ttlMs: number, now: number): boolean {
  const updated = Date.parse(record.updatedAt);

  if (Number.isNaN(updated)) {
    return true;
  }

  return now - updated > ttlMs;
}

export interface RecordExperienceContextInput {
  eventId?: string;
  worldId?: string;
  entities: ReportCorrelationEntities;
  outcome: ExperienceOutcome;
}

/**
 * Upsert the current attempt's context (called as the streaming ids become
 * known and when the session goes active). Merges entities so a later partial
 * update doesn't drop earlier ids.
 */
export function recordExperienceContext(
  input: RecordExperienceContextInput,
  options: { storage?: Storage; now?: () => number } = {},
): void {
  const store = getStore(options.storage);
  const now = options.now ?? ((): number => Date.now());
  const existing = safeRead(store);

  const record: ExperienceDiagnostics = {
    eventId: input.eventId ?? existing?.eventId,
    worldId: input.worldId ?? existing?.worldId,
    entities: { ...(existing?.entities ?? {}), ...pruneUndefined(input.entities) },
    outcome: input.outcome,
    // Clear a prior failure reason once a new attempt goes active/attempting.
    failureReason: input.outcome === 'failed' ? existing?.failureReason : undefined,
    updatedAt: new Date(now()).toISOString(),
  };

  safeWrite(store, record);
}

/**
 * Stamp the reason an attempt ended (called from the failure → /events funnel).
 * Updates the existing record so the report shows what went wrong; creates a
 * minimal record if somehow none exists yet.
 */
export function recordExperienceFailure(
  reason: string,
  options: { storage?: Storage; now?: () => number } = {},
): void {
  const store = getStore(options.storage);
  const now = options.now ?? ((): number => Date.now());
  const existing = safeRead(store);

  const record: ExperienceDiagnostics = {
    eventId: existing?.eventId,
    worldId: existing?.worldId,
    entities: existing?.entities ?? {},
    outcome: 'failed',
    failureReason: reason,
    updatedAt: new Date(now()).toISOString(),
  };

  safeWrite(store, record);
}

/** Read the last attempt's diagnostics, or null when absent / stale. */
export function readExperienceDiagnostics(
  options: { storage?: Storage; now?: () => number; ttlMs?: number } = {},
): ExperienceDiagnostics | null {
  const store = getStore(options.storage);
  const now = options.now ? options.now() : Date.now();
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const record = safeRead(store);

  if (!record) {
    return null;
  }

  if (isStale(record, ttlMs, now)) {
    return null;
  }

  return record;
}

/** Clear the cache (e.g. after a report is filed against it). */
export function clearExperienceDiagnostics(options: { storage?: Storage } = {}): void {
  const store = getStore(options.storage);

  if (!store) {
    return;
  }

  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/** Drop undefined keys so a merge doesn't overwrite a known id with undefined. */
function pruneUndefined(
  entities: ReportCorrelationEntities,
): ReportCorrelationEntities {
  const out: ReportCorrelationEntities = {};

  (Object.keys(entities) as Array<keyof ReportCorrelationEntities>).forEach((key) => {
    const value = entities[key];

    if (value !== undefined && value !== null && value !== '') {
      out[key] = value;
    }
  });

  return out;
}
